/**
 * API of the ABCDior Settings screen: info message, notifications, screensavers
 * and backgrounds.
 */
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api, request } from '@/shared/api/client';
import { getToken } from '@/features/auth/session';
import type { Platform } from '@/shared/constants';
import type { InfoMessage } from './info-message';

export { infoMessageQueryOptions } from './info-message';

export interface InfoMessageUpdate {
  isEnabled: boolean;
  message: string;
  level: string;
  displayOn: string;
  platform: Platform;
  threadFilterEnabled: boolean;
}

/** The API answers `{ error }` with a 200 on failure: surfaced as such. */
export type ApiResult<T> = T | { error: string };

export const hasError = (data: unknown): data is { error: string } =>
  !!data &&
  typeof data === 'object' &&
  typeof (data as { error?: unknown }).error === 'string';

export const updateInfoMessage = (model: InfoMessageUpdate) =>
  api.put<ApiResult<InfoMessage>>('config/info', model);

// --- notifications (MTM) ---

export const notificationSchema = z.object({
  id: z.string().optional(),
  _id: z.string().optional(),
  message: z.string().nullish(),
  type: z.string().nullish(),
  platform: z.string().nullish(),
  createdAt: z.string().nullish(),
});

export type Notification = z.infer<typeof notificationSchema>;

export const notificationsQueryOptions = (platform: Platform) =>
  queryOptions({
    queryKey: ['notifications', platform] as const,
    queryFn: () =>
      api.get<Notification[]>('notification', {
        query: { platform },
        schema: z.array(notificationSchema),
      }),
    retry: false,
  });

export const createNotification = (model: {
  platform: Platform;
  message: string;
}) => api.post<ApiResult<Notification>>('notification', model);

// --- screensavers ---

export const assetSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
  url: z.string().nullish(),
  active: z.boolean().nullish(),
  type: z.string().nullish(),
  assignedStores: z.array(z.string().nullable()).nullish(),
  createdAt: z.string().nullish(),
});

export type Asset = z.infer<typeof assetSchema>;

export const screenSaversQueryOptions = () =>
  queryOptions({
    queryKey: ['screen-savers'] as const,
    queryFn: () =>
      api.get<Asset[]>('screen-saver', { schema: z.array(assetSchema) }),
    retry: false,
  });

export interface ScreenSaverInput {
  name: string;
  url: string;
  active: boolean;
  assignedStores: string[];
}

export const createScreenSaver = (model: ScreenSaverInput) =>
  api.post<Asset>('screen-saver', { ...model, type: 'screensaver' });
export const updateScreenSaver = (id: string, model: ScreenSaverInput) =>
  api.put<Asset>(`screen-saver/${encodeURIComponent(id)}`, {
    id,
    ...model,
    type: 'screensaver',
  });

// --- backgrounds ---

export const backgroundsQueryOptions = () =>
  queryOptions({
    queryKey: ['backgrounds'] as const,
    queryFn: () =>
      api.get<Asset[]>('background', {
        query: { type: 'background' },
        schema: z.array(assetSchema),
      }),
    retry: false,
  });

export interface BackgroundInput {
  name: string;
  active: boolean;
  assignedStores: string[];
}

export const createBackground = (model: BackgroundInput) =>
  api.post<Asset>('background', { ...model, type: 'background' });
export const updateBackground = (id: string, model: BackgroundInput) =>
  api.put<Asset>(`background/${encodeURIComponent(id)}`, {
    id,
    ...model,
    type: 'background',
  });

/** Multipart upload, outside the JSON client: the browser sets the boundary itself. */
export const uploadBackgroundPicture = async (
  id: string,
  file: File,
  pictureType = 'background',
): Promise<void> => {
  const formData = new FormData();
  formData.append('file', file);
  const headers = new Headers();
  const token = getToken();
  if (token) headers.set('Authorization', token);
  const response = await fetch(
    `/api/background/${encodeURIComponent(id)}/upload/${pictureType}`,
    {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'same-origin',
    },
  );
  if (!response.ok)
    throw new Error(`Upload failed with status ${response.status}`);
};

export const deleteBackgroundPicture = (
  id: string,
  pictureType = 'background',
) =>
  request(`background/${encodeURIComponent(id)}/${pictureType}`, {
    method: 'DELETE',
  });
